<script setup lang="ts">
import type { HTMLAttributes } from 'vue'
import { PhX } from '@phosphor-icons/vue'
import { cn } from '@/lib/utils'

interface Props {
  variant?: 'primary' | 'pi' | 'success' | 'warning' | 'danger' | 'gray' | 'default' | 'outline' | 'destructive' | 'secondary'
  size?: 'sm' | 'md'
  dot?: boolean
  removable?: boolean
  color?: string
  class?: HTMLAttributes['class']
}

defineOptions({
  name: 'BaseBadge',
})

const props = withDefaults(defineProps<Props>(), {
  variant: 'primary',
  size: 'md',
  dot: false,
  removable: false,
})

const emit = defineEmits<{
  remove: []
}>()

const colorClasses: Record<string, string> = {
  green: 'bg-emerald-500/15 text-accent-emerald border-emerald-500/30',
  emerald: 'bg-emerald-500/15 text-accent-emerald border-emerald-500/30',
  red: 'bg-red-500/15 text-accent-red border-red-500/30',
  blue: 'bg-blue-500/15 text-accent-blue border-blue-500/30',
  yellow: 'bg-amber-500/15 text-accent-amber border-amber-500/30',
  amber: 'bg-amber-500/15 text-accent-amber border-amber-500/30',
  gray: 'bg-surface-2 text-ink-4 border-line-2',
  slate: 'bg-surface-2 text-ink-4 border-line-2',
  cyan: 'bg-cyan-500/15 text-accent-cyan border-cyan-500/30',
  purple: 'bg-purple-500/15 text-accent-purple border-purple-500/30',
  orange: 'bg-orange-500/15 text-accent-orange border-orange-500/30',
}

const variantClasses: Record<string, string> = {
  primary: 'bg-cyan-500/10 text-accent-cyan border border-cyan-500/20',
  pi: 'bg-purple-500/10 text-accent-purple border border-purple-500/20',
  success: 'bg-emerald-500/10 text-accent-emerald border border-emerald-500/20',
  warning: 'bg-amber-500/10 text-accent-amber border border-amber-500/20',
  danger: 'bg-red-500/10 text-accent-red border border-red-500/20',
  gray: 'bg-surface-2 text-ink-4 border border-line-2',
  default: 'bg-primary text-primary-foreground border-transparent',
  destructive: 'bg-destructive text-white border-transparent',
  outline: 'border border-border text-foreground bg-transparent',
  secondary: 'bg-secondary text-secondary-foreground border-transparent',
}

const dotClasses: Record<string, string> = {
  primary: 'bg-cyan-400',
  pi: 'bg-purple-400',
  success: 'bg-emerald-400',
  warning: 'bg-amber-400',
  danger: 'bg-red-400',
  gray: 'bg-surface-5',
}

const sizeClasses = {
  sm: 'px-1.5 py-0.5 text-xs',
  md: 'px-2 py-0.5 text-xs font-medium',
}
</script>

<template>
  <span
    :class="cn(
      'badge inline-flex items-center gap-1 rounded-full border transition-colors',
      props.color ? colorClasses[props.color] || colorClasses.gray : variantClasses[props.variant] || variantClasses.primary,
      sizeClasses[props.size],
      props.class,
    )"
  >
    <span v-if="props.dot" :class="cn('w-1.5 h-1.5 rounded-full', dotClasses[props.variant] || 'bg-cyan-400')" />
    <slot />
    <button
      v-if="props.removable"
      type="button"
      @click="emit('remove')"
      class="w-auto ml-0.5 p-0.5 rounded hover:bg-tint-hover transition-colors"
      aria-label="Remove"
    >
      <PhX class="w-3 h-3" />
    </button>
  </span>
</template>
